import {
  CALLBACK_CORRELATION_ID_HEADER,
  CallbackCommunicationRequestHeaders,
  getCallbackCorrelationHeaders,
} from '../models/callback';

type CallbackTelemetryHeadersSource =
  | Headers
  | readonly string[][]
  | Record<string, string | undefined>
  | null
  | undefined;

interface CallbackTelemetryRequestContext {
  url?: string | null;
  requestHeaders?: CallbackTelemetryHeadersSource;
}

const CALLBACK_URL_TOKEN = 'callback';

const getRequestPath = (url?: string | null): string => {
  if (!url) {
    return '';
  }

  return url.split('?')[0].split('#')[0].trim().toLowerCase();
};

const getHeaderValue = (
  requestHeaders?: CallbackTelemetryHeadersSource,
  headerName: string = CALLBACK_CORRELATION_ID_HEADER,
): string | undefined => {
  if (!requestHeaders) {
    return undefined;
  }

  if (requestHeaders instanceof Headers) {
    return requestHeaders.get(headerName) ?? requestHeaders.get(headerName.toLowerCase()) ?? undefined;
  }

  if (Array.isArray(requestHeaders)) {
    const headerEntry = requestHeaders.find(([key]) => key.toLowerCase() === headerName.toLowerCase());

    return headerEntry?.[1];
  }

  for (const [key, value] of Object.entries(requestHeaders)) {
    if (key.toLowerCase() === headerName.toLowerCase()) {
      return value;
    }
  }

  return undefined;
};

const createCallbackCorrelationId = (): string => {
  if (typeof globalThis !== 'undefined' && typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  return `callback-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const isCallbackTelemetryRequest = ({ url, requestHeaders }: CallbackTelemetryRequestContext): boolean => {
  if (getHeaderValue(requestHeaders, CALLBACK_CORRELATION_ID_HEADER)) {
    return true;
  }

  return getRequestPath(url).includes(CALLBACK_URL_TOKEN);
};

export const getCallbackTelemetryHeaders = ({
  url,
  requestHeaders,
}: CallbackTelemetryRequestContext): CallbackCommunicationRequestHeaders | undefined => {
  if (!isCallbackTelemetryRequest({ url, requestHeaders })) {
    return undefined;
  }

  const correlation_id = getHeaderValue(requestHeaders, CALLBACK_CORRELATION_ID_HEADER) ?? createCallbackCorrelationId();

  return getCallbackCorrelationHeaders({ correlation_id });
};

export const applyCallbackTelemetryHeaders = (
  headers: Headers,
  context: CallbackTelemetryRequestContext,
): Headers => {
  const callbackTelemetryHeaders = getCallbackTelemetryHeaders({
    ...context,
    requestHeaders: context.requestHeaders ?? headers,
  });

  if (!callbackTelemetryHeaders) {
    return headers;
  }

  const correlationId = callbackTelemetryHeaders[CALLBACK_CORRELATION_ID_HEADER];

  headers.set(CALLBACK_CORRELATION_ID_HEADER, correlationId);

  return headers;
};
